import type { OfficialVaultStatus } from 'src/pages/admin/services/officialVaultService';

import { VAULT_PARAMS_ERROR } from '../params/constants';

export const OFFICIAL_VAULTS_PERMISSION_RESOURCE = 'vault:official-vaults';

export const OFFICIAL_VAULTS_NS = 'module.officialVaults';

/** Design §3 — mirrors the engine; the engine stays the authority. */
export const VAULT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/; // catalog chk_vault_id
export const VAULT_ID_MAX = 63;
export const VAULT_ID_PREFIX = 'official-';
export const NAME_MIN = 2;
export const NAME_MAX = 24; // Module PRD §9
/** Module PRD §14.3 "Description": 20–200 characters, counted in code points as the engine does. */
export const STRATEGY_MIN = 20;
export const STRATEGY_MAX = 200;
export const X_HANDLE_MAX = 64;
export const GROUP_CHAT_ID_MAX = 128;
/** The fee is typed as a percent: 6 dp as a fraction is 4 dp as a percent. */
export const FEE_PERCENT_MAX_DECIMALS = 4;
/** C-08: ratios travel at 6 dp exactly. */
export const WIRE_DECIMALS = 6;
/** The stake is USDC, typed as it travels: at most 6 dp. */
export const STAKE_MAX_DECIMALS = 6;
/** The manager's UID after lower-casing: a canonical UUID, as the BFF DTO and the engine require. */
export const MANAGER_UID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** The manager's HyperCore address: 0x + 40 hex, any case (the engine lower-cases it). */
export const MANAGER_ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/;

export const OFFICIAL_VAULT_STATUSES: readonly OfficialVaultStatus[] = [
  'REQUESTED',
  'ACCOUNT_READY',
  'CATALOGUED',
  'ACTIVATED',
  'PENDING_REVIEW',
  'OPEN',
  'CANCELLED',
  'FAILED',
];

/** The provisioning steps in order (C-08); the details drawer shows how far a request got. */
export const PROVISIONING_STEPS: readonly OfficialVaultStatus[] = [
  'REQUESTED',
  'ACCOUNT_READY',
  'CATALOGUED',
  'ACTIVATED',
  'PENDING_REVIEW',
  'OPEN',
];

/** D-30 review gate (STORY-22): approve once the manager's stake is parked; reject before or at the review. */
export const APPROVE_STATUSES: readonly OfficialVaultStatus[] = ['PENDING_REVIEW'];
export const REJECT_STATUSES: readonly OfficialVaultStatus[] = [
  'CATALOGUED',
  'ACTIVATED',
  'PENDING_REVIEW',
];
/** The engine's reason bound (C-06): 1–500 characters after trimming, counted as code points. */
export const REJECT_REASON_MAX = 500;

/** `body.code` values of the re-drive route (XBIT-4240, C-08 v3.1). Every one is a 409 and nothing was written. */
export const REDRIVE_ERROR = {
  NOT_FAILED: 'VAULT_REDRIVE_NOT_FAILED',
  NOT_OFFICIAL: 'VAULT_REDRIVE_NOT_OFFICIAL',
  STEP_UNKNOWN: 'VAULT_REDRIVE_STEP_UNKNOWN',
  CONFLICT: 'VAULT_REDRIVE_CONFLICT',
  NAME_TAKEN: 'VAULT_REDRIVE_NAME_TAKEN',
} as const;

/** `body.code` values of the review routes (XBIT-4256). */
export const REVIEW_ERROR = {
  NOT_PENDING: 'VAULT_REVIEW_NOT_PENDING',
  CONFLICT: 'VAULT_REVIEW_CONFLICT',
  INVALID: 'VAULT_REVIEW_INVALID',
} as const;

/**
 * What the operator sees in the Status column (PO, 2026-10-01): one vocabulary per kind, the C-08 step only in the
 * details. Community: Pending / Rejected / Live (Module PRD §14.3). Official: Provisioning / Pending / Provisioning failed
 * / Live, where Pending means waiting on the operator to open it (PO, 2026-10-01, after D-30's approval step).
 */
export const DISPLAY_STATUSES = [
  'PENDING',
  'REJECTED',
  'CANCELLED',
  'LIVE',
  'FAILED',
  'PROVISIONING',
  'PROVISIONING_FAILED',
] as const;
export type DisplayStatus = (typeof DISPLAY_STATUSES)[number];

/** `body.code` values of POST /vault/official-vaults. Engine transport codes are shared with /vault/params. */
export const OFFICIAL_VAULT_ERROR = {
  INVALID: 'VAULT_ONBOARDING_INVALID',
  CONFLICT: 'VAULT_ONBOARDING_CONFLICT',
  /** Recorded, not published — the same values submitted again re-publish. */
  NOT_PUBLISHED: 'VAULT_ONBOARDING_NOT_PUBLISHED',
  ENGINE_UNAVAILABLE: VAULT_PARAMS_ERROR.ENGINE_UNAVAILABLE,
  ENGINE_AUTH_FAILED: VAULT_PARAMS_ERROR.ENGINE_AUTH_FAILED,
  ENGINE_TIMEOUT: VAULT_PARAMS_ERROR.ENGINE_TIMEOUT,
  ENGINE_NOT_CONFIGURED: VAULT_PARAMS_ERROR.ENGINE_NOT_CONFIGURED,
} as const;

/**
 * The C-08 statuses an official vault waits in before its review: still DRAFT, the stake not yet parked. Pending in the
 * list (the operator has something coming), with nothing to open: approve is the only door (STORY-22 AC15).
 */
export const PRE_REVIEW_STATUSES: readonly OfficialVaultStatus[] = ['CATALOGUED', 'ACTIVATED'];

/** XBIT-4348 testnet faucet, its own Casbin resource (admin-service seeds `{vault:faucet, create}`). */
export const FAUCET_PERMISSION_RESOURCE = 'vault:faucet';

/**
 * The only network the faucet is offered on. Spelled as `venueconst.Network` does, and matched exactly:
 * the engine's own parser refuses `TESTNET` and ` Testnet`, so a lenient match here would offer an action
 * the engine refuses.
 */
export const FAUCET_TESTNET_NETWORK = 'Testnet';

/** Hyperliquid's own spot coin name for USDC in `spotClearinghouseState` (measured on testnet 2026-10-02). */
export const HL_USDC_COIN = 'USDC';

/** The catalog states that take a faucet transfer: DRAFT takes the manager's stake, PAUSED and CLOSED must not take money. */
export const FAUCET_VAULT_STATES: readonly string[] = ['DRAFT', 'RAISING', 'ACTIVE'];

/** `body.code` values of the faucet routes. The engine's specific `Faucet_*` rides in `upstreamCode`. */
export const FAUCET_ERROR = {
  NOT_ENABLED: 'VAULT_FAUCET_NOT_ENABLED',
  VAULT_NOT_FOUND: 'VAULT_FAUCET_VAULT_NOT_FOUND',
  INVALID: 'VAULT_FAUCET_INVALID',
  CONFLICT: 'VAULT_FAUCET_CONFLICT',
  UPSTREAM: 'VAULT_FAUCET_UPSTREAM',
  ENGINE_UNAVAILABLE: VAULT_PARAMS_ERROR.ENGINE_UNAVAILABLE,
  ENGINE_AUTH_FAILED: VAULT_PARAMS_ERROR.ENGINE_AUTH_FAILED,
} as const;
